/**
 * Mobile API - PWA / Android app endpoints
 * @module api/mobile
 */

import { MOBILE_API_BASE, getMobileToken } from './core.js';

// =============================================================================
// MOBILE API HELPER
// =============================================================================

/**
 * Mobile API request with auto-refresh
 */
async function mobileApiRequest(endpoint, options = {}) {
  const token = getMobileToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${MOBILE_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  // Handle 401 - could trigger refresh here
  if (response.status === 401) {
    // Try refresh token
    const refreshToken = localStorage.getItem('fm_mobile_refresh_token');
    if (refreshToken && !endpoint.includes('/auth/')) {
      const refreshResult = await mobileAPI.refreshToken(refreshToken);
      if (refreshResult.ok) {
        localStorage.setItem('fm_mobile_access_token', refreshResult.access_token);
        localStorage.setItem('fm_mobile_refresh_token', refreshResult.refresh_token);
        // Retry original request
        return mobileApiRequest(endpoint, options);
      }
    }
    throw new Error('Session expired');
  }

  return data;
}

// =============================================================================
// MOBILE API
// =============================================================================

/**
 * Mobile API for FleetManager PWA/Android app
 */
export const mobileAPI = {
  // Auth
  login: async (email, password) => {
    const response = await fetch(`${MOBILE_API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  refreshToken: async (refreshToken) => {
    const response = await fetch(`${MOBILE_API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    return response.json();
  },

  logout: async (refreshToken) => {
    try {
      await fetch(`${MOBILE_API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getMobileToken()}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Ignore logout errors
    }
  },

  // Vehicles
  getVehicles: (params = {}) => {
    const query = new URLSearchParams();
    if (params.alertsOnly) query.set('alertsOnly', '1');
    if (params.scope) query.set('scope', params.scope);
    if (params.page) query.set('page', params.page);
    return mobileApiRequest(`/vehicles?${query.toString()}`);
  },

  getVehicleSummary: (id) => mobileApiRequest(`/vehicles/${id}/summary`),

  // Todos
  getTodos: (params = {}) => {
    const query = new URLSearchParams();
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.vehicleId) query.set('vehicleId', params.vehicleId);
    if (params.page) query.set('page', params.page);
    return mobileApiRequest(`/todos?${query.toString()}`);
  },

  updateTodo: (id, data) =>
    mobileApiRequest(`/todos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Documents
  getDocuments: (params = {}) => {
    const query = new URLSearchParams();
    if (params.vehicleId) query.set('vehicleId', params.vehicleId);
    if (params.status) query.set('status', params.status);
    if (params.page) query.set('page', params.page);
    return mobileApiRequest(`/documents?${query.toString()}`);
  },

  reviewDocument: (id, data) =>
    mobileApiRequest(`/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Document upload - initialize
  initDocumentUpload: (metadata) =>
    mobileApiRequest('/documents/init-upload', {
      method: 'POST',
      body: JSON.stringify(metadata),
    }),

  // Document upload - complete with file
  uploadDocument: async (file, metadata) => {
    const token = getMobileToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', metadata.scope || 'VEHICLE');
    formData.append('reference_id', metadata.reference_id);
    formData.append('document_type', metadata.document_type || 'photo');
    if (metadata.vehicle_id) formData.append('vehicle_id', metadata.vehicle_id);
    if (metadata.todo_id) formData.append('todo_id', metadata.todo_id);
    if (metadata.notes) formData.append('notes', metadata.notes);
    if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));

    const response = await fetch(`${MOBILE_API_BASE}/documents/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    return response.json();
  },

  // Get document types
  getDocumentTypes: () => mobileApiRequest('/documents/types'),

  // Alerts
  getAlerts: (params = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    return mobileApiRequest(`/alerts?${query.toString()}`);
  },

  markAlertRead: (id) =>
    mobileApiRequest(`/alerts/${id}/read`, { method: 'POST' }),

  // Profile
  getProfile: () => mobileApiRequest('/profile'),
};
